import React, { useState, useMemo } from 'react';
import {
  FaSearch, FaCheck, FaTimes, FaBox, FaClock,
  FaHandshake, FaCommentDots, FaEuroSign, FaSort,
  FaTh, FaList,
} from 'react-icons/fa';
import { useProContext } from '../context/ProContext';
import ProLayout from '../layout/ProLayout';
import MessageDrawer from '../components/MessageDrawer';
import ApproveModal from '../modals/ApproveModal';
import RejectModal from '../modals/RejectModal';
import FreeAmountPaymentModal from '../modals/FreeAmountPaymentModal';

const OngoingPage = () => {
  const {
    currentUser, brand,
    ongoing,
    messages, unreadMessages, fetchMessages, sendMessage, markAsRead,
    clients, updateClientStatus, loadStats, dm, fetchClients,
  } = useProContext();

  const [selectedClient, setSelectedClient] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const [freeAmountData, setFreeAmountData] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | refund
  const [periodFilter, setPeriodFilter] = useState('all'); // all | today | week | month
  const [sortBy, setSortBy] = useState('recent'); // recent | oldest | name
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'cards';
    return localStorage.getItem('ongoingViewMode') || 'cards';
  }); // cards | rows
  const setView = (mode) => {
    setViewMode(mode);
    if (typeof window !== 'undefined') localStorage.setItem('ongoingViewMode', mode);
  };

  const isInPeriod = (dateStr, period) => {
    if (period === 'all') return true;
    const d = new Date(dateStr);
    const now = new Date();
    if (period === 'today') return d.toDateString() === now.toDateString();
    if (period === 'week') {
      const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7);
      return d >= weekAgo;
    }
    if (period === 'month') {
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }
    return true;
  };

  // Filtered + sorted list
  const filtered = useMemo(() => {
    let list = ongoing.filter(c => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (!isInPeriod(c.created_at, periodFilter)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const name = `${c.first_name} ${c.last_name}`.toLowerCase();
        const sponsor = (c.sponsor_name || '').toLowerCase();
        if (!name.includes(q) && !sponsor.includes(q)) return false;
      }
      return true;
    });

    // Sort
    list = [...list].sort((a, b) => {
      if (sortBy === 'recent') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      if (sortBy === 'name') return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
      return 0;
    });

    return list;
  }, [ongoing, statusFilter, periodFilter, search, sortBy]);

  const activeFilters = periodFilter !== 'all' ? 1 : 0;
  const resetAllFilters = () => {
    setPeriodFilter('all');
    setSearch('');
    setStatusFilter('all');
  };

  const pendingCount = ongoing.filter(c => c.status === 'pending').length;
  const inProgressCount = ongoing.filter(c => c.status === 'refund').length;

  const selectClient = (c) => { setSelectedClient(c); setDrawerOpen(true); markAsRead(c.id); };

  const formatDate = (d) => {
    const date = new Date(d);
    const now = new Date();
    const diffH = Math.floor((now - date) / 3600000);
    if (diffH < 1) return "À l'instant";
    if (diffH < 24) return `Il y a ${diffH}h`;
    if (diffH < 48) return 'Hier';
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `Il y a ${diffD} jours`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  };

  const statusBadge = (client) => {
    // A pro who already submitted a rejection request is awaiting the
    // sponsor's reply — surface that explicitly instead of showing the
    // generic "En attente" badge that would suggest no action has been
    // taken yet.
    if (client.status === 'pending' && client.rejection_requested) {
      return (
        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[0.625rem] font-semibold bg-orange-50 text-orange-600 border-orange-200">
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#F97316' }} />
          En attente du parrain
        </div>
      );
    }
    const map = {
      pending: { label: 'En attente', cls: 'bg-amber-50 text-amber-600 border-amber-200', dot: '#F59E0B' },
      refund: { label: 'En cours', cls: 'bg-blue-50 text-blue-600 border-blue-200', dot: '#3B82F6' },
      payed: { label: 'Payé', cls: 'bg-purple-50 text-purple-600 border-purple-200', dot: '#8B5CF6' },
    };
    const s = map[client.status] || { label: client.status, cls: 'bg-gray-50 text-gray-600 border-gray-200', dot: '#6B7280' };
    return (
      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[0.625rem] font-semibold ${s.cls}`}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.dot }} />
        {s.label}
      </div>
    );
  };

  const unreadForClient = (clientId) => unreadMessages.filter(m => m.client_id === clientId).length;

  const handleAction = async (client, action) => {
    setSelectedClient(client);
    switch (action) {
      case 'approve': setModal('approve'); break;
      case 'reject': setModal('reject'); break;
      case 'refund': await updateClientStatus(client.id, '/clients/refund-status/:id'); await loadStats(dm, true); break;
      case 'delivered': await updateClientStatus(client.id, '/clients/delivered-status/:id'); await loadStats(dm, true); break;
      default: break;
    }
  };

  const filters = [
    { key: 'all', label: 'Toutes', count: ongoing.length, color: '#6B7280' },
    { key: 'pending', label: 'En attente', count: pendingCount, color: '#F59E0B' },
    { key: 'refund', label: 'En cours', count: inProgressCount, color: '#3B82F6' },
  ];

  return (
    <ProLayout title="Recommandations en cours">
      <div className="p-5 space-y-4">

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
            <p className="text-[0.6875rem] text-gray-400">Total en cours</p>
            <p className="text-xl font-bold text-gray-900">{ongoing.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
            <p className="text-[0.6875rem] text-gray-400">À valider</p>
            <p className="text-xl font-bold text-amber-600">{pendingCount}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
            <p className="text-[0.6875rem] text-gray-400">En cours</p>
            <p className="text-xl font-bold text-blue-600">{inProgressCount}</p>
          </div>
        </div>

        {/* Search bar */}
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un client ou un recommandeur..."
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
            />
          </div>
          {/* Sort */}
          <div className="relative">
            <FaSort className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-300 pointer-events-none" />
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="pl-8 pr-8 py-2.5 bg-white border border-gray-200 rounded-xl text-[0.75rem] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300 appearance-none cursor-pointer"
            >
              <option value="recent">Plus récents</option>
              <option value="oldest">Plus anciens</option>
              <option value="name">Par nom</option>
            </select>
          </div>
          {/* View toggle */}
          <div className="flex items-center bg-white border border-gray-200 rounded-xl p-0.5">
            <button
              onClick={() => setView('cards')}
              title="Vue cartes"
              className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${viewMode === 'cards' ? 'bg-gray-900 text-white' : 'text-gray-400 hover:text-gray-700'}`}
            >
              <FaTh className="w-3 h-3" />
            </button>
            <button
              onClick={() => setView('rows')}
              title="Vue liste"
              className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${viewMode === 'rows' ? 'bg-gray-900 text-white' : 'text-gray-400 hover:text-gray-700'}`}
            >
              <FaList className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Status filter pills */}
        <div className="flex items-center gap-2 flex-wrap">
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.75rem] font-medium transition-all border ${statusFilter === f.key ? 'text-white border-transparent' : 'text-gray-600 border-gray-200 bg-white hover:border-gray-300'}`}
              style={statusFilter === f.key ? { backgroundColor: f.color } : {}}
            >
              {f.label}
              <span className={`text-[0.625rem] px-1.5 py-0.5 rounded-full font-bold ${statusFilter === f.key ? 'bg-white/20' : 'bg-gray-100'}`}>
                {f.count}
              </span>
            </button>
          ))}
        </div>

        {/* Advanced filters row */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Period filter */}
          <div className="flex items-center gap-2">
            <FaClock className="w-3 h-3 text-gray-400" />
            <span className="text-[0.6875rem] font-semibold text-gray-500 uppercase tracking-wide">Période</span>
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-0.5">
              {[
                { key: 'all', label: 'Tout' },
                { key: 'today', label: "Aujourd'hui" },
                { key: 'week', label: '7 jours' },
                { key: 'month', label: 'Ce mois' },
              ].map(p => (
                <button
                  key={p.key}
                  onClick={() => setPeriodFilter(p.key)}
                  className={`px-2.5 py-1 rounded text-[0.6875rem] font-medium transition-colors ${periodFilter === p.key ? 'text-white' : 'text-gray-500 hover:text-gray-700'}`}
                  style={periodFilter === p.key ? { backgroundColor: brand } : {}}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Reset */}
          {(activeFilters > 0 || search || statusFilter !== 'all') && (
            <button
              onClick={resetAllFilters}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-[0.6875rem] font-medium text-gray-500 hover:text-red-500 transition-colors"
            >
              <FaTimes className="w-2.5 h-2.5" />
              Réinitialiser
            </button>
          )}
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between">
          <p className="text-[0.75rem] text-gray-500">
            <strong className="text-gray-900">{filtered.length}</strong> {filtered.length > 1 ? 'résultats' : 'résultat'}
            {filtered.length !== ongoing.length && <span className="text-gray-400"> sur {ongoing.length}</span>}
          </p>
        </div>

        {/* Empty state */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 py-16 text-center">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: brand + '10' }}>
              <FaHandshake className="w-6 h-6" style={{ color: brand }} />
            </div>
            <p className="text-sm font-semibold text-gray-900">
              {search.trim() || statusFilter !== 'all' ? 'Aucun résultat' : 'Aucune recommandation en cours'}
            </p>
            <p className="text-[0.75rem] text-gray-400 mt-1">
              {search.trim() || statusFilter !== 'all' ? 'Essayez d\'autres filtres' : 'Les nouvelles recommandations apparaîtront ici'}
            </p>
          </div>
        ) : viewMode === 'rows' ? (
          /* ═══ Rows list ═══ */
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
            {filtered.map(c => {
              const unread = unreadForClient(c.id);
              return (
                <div key={c.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-[0.75rem] flex-shrink-0" style={{ backgroundColor: brand }}>
                    {(c.first_name || '?')[0]}{(c.last_name || '?')[0]}
                  </div>

                  {/* Name + sponsor */}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-gray-900 truncate">{c.first_name} {c.last_name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <FaHandshake className="w-2.5 h-2.5 text-gray-300 flex-shrink-0" />
                      <p className="text-[0.6875rem] text-gray-500 truncate">
                        Recommandé par <strong className="text-gray-700">{c.sponsor_name || 'Inconnu'}</strong>
                      </p>
                    </div>
                  </div>

                  {/* Visite prévue (si renseignée) */}
                  {c.delay && (
                    <div className="hidden lg:flex items-center gap-1.5 text-[0.6875rem] text-gray-500 min-w-0 max-w-[180px]">
                      <FaBox className="w-2.5 h-2.5 text-gray-300 flex-shrink-0" />
                      <span className="truncate">{c.delay.replace(/(\d{2}\/\d{2}\/\d{4})(Matin|Après-midi)/, '$1 $2')}</span>
                    </div>
                  )}

                  {/* Date */}
                  <div className="hidden md:flex items-center gap-1.5 text-[0.6875rem] text-gray-400 w-24 justify-end">
                    <FaClock className="w-2.5 h-2.5 text-gray-300" />
                    <span>{formatDate(c.created_at)}</span>
                  </div>

                  {/* Status */}
                  <div className="flex-shrink-0">{statusBadge(c)}</div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => selectClient(c)}
                      className="relative flex items-center gap-1 px-2 py-1 text-[0.625rem] font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                      title="Messages"
                    >
                      <FaCommentDots className="w-2.5 h-2.5" />
                      {unread > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[0.875rem] h-[0.875rem] bg-red-500 text-white text-[0.5rem] font-bold rounded-full flex items-center justify-center px-1">
                          {unread}
                        </span>
                      )}
                    </button>

                    {c.status === 'pending' && !c.rejection_requested && (
                      <>
                        <button
                          onClick={() => handleAction(c, 'reject')}
                          className="flex items-center gap-1 px-2 py-1 text-[0.625rem] font-semibold text-red-600 bg-red-50 border border-red-100 rounded-md hover:bg-red-100 transition-colors"
                          title="Rejeter"
                        >
                          <FaTimes className="w-2 h-2" />
                        </button>
                        <button
                          onClick={() => handleAction(c, 'refund')}
                          className="flex items-center gap-1 px-2 py-1 text-[0.625rem] font-semibold text-blue-600 bg-blue-50 border border-blue-100 rounded-md hover:bg-blue-100 transition-colors"
                          title="Visite OK"
                        >
                          <FaCheck className="w-2 h-2" /> Visite
                        </button>
                        <button
                          onClick={() => handleAction(c, 'approve')}
                          className="flex items-center gap-1 px-2 py-1 text-[0.625rem] font-semibold text-white rounded-md hover:opacity-90 transition-colors"
                          style={{ backgroundColor: brand }}
                          title="Approuver"
                        >
                          <FaCheck className="w-2 h-2" /> Approuver
                        </button>
                      </>
                    )}
                    {c.status === 'refund' && (
                      <>
                        <button
                          onClick={() => handleAction(c, 'reject')}
                          className="flex items-center gap-1 px-2 py-1 text-[0.625rem] font-semibold text-red-600 bg-red-50 border border-red-100 rounded-md hover:bg-red-100 transition-colors"
                          title="Rejeter"
                        >
                          <FaTimes className="w-2 h-2" />
                        </button>
                        <button
                          onClick={() => handleAction(c, 'approve')}
                          className="flex items-center gap-1 px-2 py-1 text-[0.625rem] font-semibold text-white rounded-md hover:opacity-90 transition-colors"
                          style={{ backgroundColor: brand }}
                          title="Approuver"
                        >
                          <FaCheck className="w-2 h-2" /> Approuver
                        </button>
                      </>
                    )}
                    {c.status === 'payed' && (
                      <button
                        onClick={() => handleAction(c, 'delivered')}
                        className="flex items-center gap-1 px-2 py-1 text-[0.625rem] font-semibold text-white rounded-md hover:opacity-90 transition-colors"
                        style={{ backgroundColor: brand }}
                        title="Livré"
                      >
                        <FaBox className="w-2 h-2" /> Livré
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ═══ Cards grid ═══ */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map(c => {
              const unread = unreadForClient(c.id);
              return (
                <div key={c.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-gray-300 transition-colors">
                  {/* Header */}
                  <div className="flex items-start justify-between p-4 pb-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-[0.8125rem] flex-shrink-0" style={{ backgroundColor: brand }}>
                        {(c.first_name || '?')[0]}{(c.last_name || '?')[0]}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-gray-900 truncate">{c.first_name} {c.last_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <FaClock className="w-2.5 h-2.5 text-gray-300" />
                          <p className="text-[0.6875rem] text-gray-400">{formatDate(c.created_at)}</p>
                        </div>
                      </div>
                    </div>
                    {statusBadge(c)}
                  </div>

                  {/* Recommandé par */}
                  <div className="px-4 pb-3">
                    <div className="flex items-center gap-2 text-[0.75rem] text-gray-500">
                      <FaHandshake className="w-3 h-3 text-gray-300" />
                      <span>Recommandé par <strong className="text-gray-700">{c.sponsor_name || 'Inconnu'}</strong></span>
                    </div>
                    {c.delay && (
                      <div className="flex items-center gap-2 text-[0.75rem] text-gray-500 mt-1">
                        <FaBox className="w-3 h-3 text-gray-300" />
                        <span>Visite prévue le <strong className="text-gray-700">{c.delay.replace(/(\d{2}\/\d{2}\/\d{4})(Matin|Après-midi)/, '$1 $2')}</strong></span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="border-t border-gray-100 px-4 py-3 flex items-center gap-2 bg-gray-50/50">
                    {/* Message button with unread badge */}
                    <button
                      onClick={() => selectClient(c)}
                      className="relative flex items-center gap-1 px-2 py-1 text-[0.625rem] font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <FaCommentDots className="w-2.5 h-2.5" />
                      Messages
                      {unread > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[0.875rem] h-[0.875rem] bg-red-500 text-white text-[0.5rem] font-bold rounded-full flex items-center justify-center px-1">
                          {unread}
                        </span>
                      )}
                    </button>

                    <div className="flex-1" />

                    {/* Action buttons based on status — hidden when a
                         rejection request is awaiting the sponsor's reply */}
                    {c.status === 'pending' && !c.rejection_requested && (
                      <>
                        <button
                          onClick={() => handleAction(c, 'reject')}
                          className="flex items-center gap-1 px-2 py-1 text-[0.625rem] font-semibold text-red-600 bg-red-50 border border-red-100 rounded-md hover:bg-red-100 transition-colors"
                          title="Rejeter cette recommandation"
                        >
                          <FaTimes className="w-2 h-2" /> Rejeter
                        </button>
                        <button
                          onClick={() => handleAction(c, 'refund')}
                          className="flex items-center gap-1 px-2 py-1 text-[0.625rem] font-semibold text-blue-600 bg-blue-50 border border-blue-100 rounded-md hover:bg-blue-100 transition-colors"
                          title="Le client est venu en boutique"
                        >
                          <FaCheck className="w-2 h-2" /> Visite OK
                        </button>
                        <button
                          onClick={() => handleAction(c, 'approve')}
                          className="flex items-center gap-1 px-2 py-1 text-[0.625rem] font-semibold text-white rounded-md hover:opacity-90 transition-colors"
                          style={{ backgroundColor: brand }}
                          title="Finaliser et valider la vente"
                        >
                          <FaCheck className="w-2 h-2" /> Approuver
                        </button>
                      </>
                    )}
                    {c.status === 'refund' && (
                      <>
                        <button
                          onClick={() => handleAction(c, 'reject')}
                          className="flex items-center gap-1 px-2 py-1 text-[0.625rem] font-semibold text-red-600 bg-red-50 border border-red-100 rounded-md hover:bg-red-100 transition-colors"
                        >
                          <FaTimes className="w-2 h-2" /> Rejeter
                        </button>
                        <button
                          onClick={() => handleAction(c, 'approve')}
                          className="flex items-center gap-1 px-2 py-1 text-[0.625rem] font-semibold text-white rounded-md hover:opacity-90 transition-colors"
                          style={{ backgroundColor: brand }}
                          title="Finaliser et valider la vente"
                        >
                          <FaCheck className="w-2 h-2" /> Approuver
                        </button>
                      </>
                    )}
                    {c.status === 'payed' && (
                      <button
                        onClick={() => handleAction(c, 'delivered')}
                        className="flex items-center gap-1 px-2 py-1 text-[0.625rem] font-semibold text-white rounded-md hover:opacity-90 transition-colors"
                        style={{ backgroundColor: brand }}
                      >
                        <FaBox className="w-2 h-2" /> Livré
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Approve / Reject modals */}
      {modal === 'approve' && selectedClient && (
        <ApproveModal
          client={selectedClient}
          currentUser={currentUser}
          onSubmit={async (data) => {
            const r = await updateClientStatus(selectedClient.id, '/clients/approved-status/:id', { ...data, commission: 0 });
            if (r?.requiresFreeAmountPayment) {
              setModal(null);
              setFreeAmountData({ ...r, clientName: `${selectedClient.first_name} ${selectedClient.last_name}`, revenue: data.revenue });
              return;
            }
            if (r?.success) { setModal(null); await loadStats(dm, true); }
          }}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'reject' && selectedClient && (
        <RejectModal
          onSubmit={async (reason) => {
            const ok = await updateClientStatus(selectedClient.id, '/clients/:id', { reason });
            if (ok) { setModal(null); fetchMessages(selectedClient.id); await loadStats(dm, true); }
          }}
          onClose={() => setModal(null)}
        />
      )}
      <FreeAmountPaymentModal
        isOpen={!!freeAmountData}
        onClose={() => setFreeAmountData(null)}
        clientId={freeAmountData?.clientId}
        sponsorId={freeAmountData?.sponsorId}
        sponsorName={freeAmountData?.sponsorName}
        userType={freeAmountData?.userType}
        clientName={freeAmountData?.clientName}
        onSuccess={async () => { setFreeAmountData(null); await fetchClients(true); await loadStats(dm, true); }}
      />

      {/* Message Drawer */}
      <MessageDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        client={selectedClient}
        messages={messages.filter(m => m.client_id === selectedClient?.id)}
        currentUser={currentUser}
        onSendMessage={async (msg) => { const sent = await sendMessage(msg); if (sent) fetchMessages(true, clients); return !!sent; }}
      />
    </ProLayout>
  );
};

export default OngoingPage;
